import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { MoneyPair } from '../../components/CashFigures';
import { currencySymbol, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { CountSheet, keyOf, type Sheet, useCountSheet } from './CountSheet';

/**
 * WHAT THE BOX IS SUPPOSED TO HOLD, note by note.
 *
 * The owner's rule for the change box is not a value, it is a composition: $1×40, $5×20, $10×10,
 * $20×10 and seven lempira lines — $440 and L 5,150. A box holding the right value in the wrong
 * notes cannot make change, which is the whole job the box exists to do, so two bare totals could
 * never state the rule. The float is typed here as quantities of each note, the same gesture as
 * every count sheet on this site, and the server works both totals out from the lines — the notes
 * and the totals cannot disagree, because only one of them is ever written.
 *
 * THE DRAWER IS COUNTED IN NOTES TOO, and it is the box the argument was always about. It opens
 * every day on $220 and L 1,500 in a stated mix, and the whole reason the cajaita exists is to buy
 * that mix back at the end of the night. This is now the only float editor on the site: the two
 * number boxes a drawer used to get are gone, because the server refuses a typed total wherever
 * lines exist and a box with no lines has no standing float rather than a float stated as a value.
 *
 * What a drawer changes here is WHEN the edit lands: a box is counted directly, so a new
 * composition is what its next count is measured against, but a drawer is counted through the shift
 * it belongs to and is measured against the float that shift OPENED on. Both sentences appear
 * below, chosen by the box's kind — one of them is false about the other box.
 */

export interface FloatLine {
  currency: 'USD' | 'HNL';
  denominationMinor: number;
  qty: number;
}

/** What the PUT hands back: the row with its lines, totals recomputed by the server. */
interface SavedBox {
  id: string;
  name: string;
  standardFloatUsdMinor: number;
  standardFloatHnlMinor: number;
  floatLines: FloatLine[];
}

/**
 * "$1×40 · $5×20 · …" — the composition read back, dollars first and small notes first, whatever
 * order the wire chose. Major units with no cents, because these are whole bills being counted out
 * on an island with no coins, and "$ 1.00×40" is arithmetic where a person wants a picture.
 */
export function noteByNote(lines: FloatLine[]): string {
  return [...lines]
    .sort(
      (a, b) =>
        (a.currency === b.currency ? 0 : a.currency === 'USD' ? -1 : 1) ||
        a.denominationMinor - b.denominationMinor,
    )
    .map((l) => `${currencySymbol(l.currency)}${l.denominationMinor / 100}×${l.qty}`)
    .join(' · ');
}

/** The sheet the modal opens on: the stored composition, so blanking a note is a deliberate act. */
const seedOf = (lines: FloatLine[]): Sheet =>
  Object.fromEntries(
    lines.map((l) => [keyOf({ currency: l.currency, valueMinor: l.denominationMinor }), l.qty]),
  );

/**
 * The float column, for every box.
 *
 * The totals are shown because they are what the balance card and the audit measure against; the
 * composition is shown under them because it is the actual rule, and a row that hid it would be
 * the old two-scalars world wearing a new door.
 */
export function BoxStandingFloat({
  box,
  tradingNow = false,
  onEdit,
}: {
  box: {
    name: string;
    standardFloatUsdMinor: number;
    standardFloatHnlMinor: number;
    floatLines: FloatLine[];
  };
  /**
   * A shift is open on this box right now, so an edit lands on somebody's evening. Only ever true
   * of a drawer, and carried here rather than left behind with the two-number editor: the note is
   * about WHEN a float change takes effect, which does not stop being a live question just because
   * the float is now stated in notes.
   */
  tradingNow?: boolean;
  onEdit: () => void;
}) {
  const none = box.standardFloatUsdMinor === 0 && box.standardFloatHnlMinor === 0;
  return (
    <Stack gap={4}>
      <Group gap="sm" wrap="nowrap">
        {none ? (
          <Text size="sm" c="dimmed">
            No standing float — it holds whatever is put into it
          </Text>
        ) : (
          <MoneyPair
            pair={{ usdMinor: box.standardFloatUsdMinor, hnlMinor: box.standardFloatHnlMinor }}
            fw={600}
          />
        )}
        <Button size="compact-xs" variant="light" onClick={onEdit}>
          Set what it holds
        </Button>
      </Group>
      {box.floatLines.length > 0 && (
        <Text size="xs" c="dimmed">
          {noteByNote(box.floatLines)}
        </Text>
      )}
      {tradingNow && (
        <Text size="xs" c="dimmed">
          A shift is open on this drawer — this takes effect when the next one opens
        </Text>
      )}
    </Stack>
  );
}

/**
 * The modal that states the composition. Mounted fresh each time it opens, so the sheet always
 * starts from what is on file rather than from whatever the last visit left behind.
 */
export function SetWhatItHolds({
  box,
  onClose,
  onSaved,
}: {
  box: { id: string; name: string; kind: 'drawer' | 'cajaita' | 'safe'; floatLines: FloatLine[] };
  onClose: () => void;
  onSaved: () => void;
}) {
  const sheet = useCountSheet(seedOf(box.floatLines));

  /*
   * The sheet is only trustworthy once the denominations have actually arrived. While the read is
   * in flight — or after it FAILED — `notes` is [] and `lines()` is [] whatever the seeded
   * composition says, so a save at that moment would PUT `floatLines: []`; the server takes an
   * empty array as a full replacement and the box's whole standing composition is silently zeroed.
   * The office wifi blinking must not be a way to erase the float, so the save stays off until a
   * successful read has produced rows to type into.
   *
   * Deliberately NOT gated on anything being typed: with the notes LOADED, blanking every box and
   * saving is the legitimate "this box holds no standing float" gesture and must keep working.
   */
  const ready = sheet.notes.length > 0;

  const save = useMutation({
    mutationFn: () =>
      rawApi<SavedBox>('PUT', `/admin/v1/cash/locations/${box.id}`, {
        /*
         * A zero typed here means the same as a blank — the note comes off the float. On a count a
         * deliberate zero is a fact ("I looked and there were none"); a composition states what the
         * box IS stocked with, so a line of nought would be a note it holds none of, which is every
         * note not on the list. The server refuses zero-quantity lines for the same reason.
         */
        floatLines: sheet.lines().filter((l) => l.qty > 0),
      }),
    onSuccess: (saved) => {
      // The recomputed totals, read back off the reply rather than re-added here: the server's
      // arithmetic is the one the balance card and the audit will use.
      notifyDone(
        'Standing float saved',
        `The ${saved.name} is set to hold ${formatMoney(saved.standardFloatUsdMinor, 'USD')} · ${formatMoney(
          saved.standardFloatHnlMinor,
          'HNL',
        )}, worked out from the notes you listed.`,
      );
      onSaved();
      onClose();
    },
    onError: (error) => notifyError(error, 'save the standing float'),
  });

  return (
    <Modal opened onClose={onClose} title={`What the ${box.name} holds`} size="lg">
      <Stack gap="sm">
        {/* WHEN IT LANDS IS NOT THE SAME SENTENCE FOR THE TWO KINDS. A box is counted directly, so a
            new composition is what its very next count is measured against. A drawer is counted
            through the shift it belongs to, against the float that shift OPENED on — telling the
            owner mid-evening that his change takes effect straight away would have him expect
            tonight's count to answer to a rule tonight was never measured by. */}
        <Text size="sm" c="dimmed">
          How many of each note this box is kept stocked with. The two totals are worked out from these lines,
          so the notes and the totals can never disagree.{' '}
          {box.kind === 'drawer'
            ? 'It takes effect at the next open — a shift is measured against the float it started with, so nothing here moves a figure on a shift already running or already counted off.'
            : 'It takes effect straight away — the next count of this box is measured against it.'}
        </Text>
        <CountSheet
          notes={sheet.notes}
          loading={sheet.loading}
          error={sheet.error}
          sheet={sheet.sheet}
          onChange={(key, value) => sheet.setSheet({ ...sheet.sheet, [key]: value })}
          blankReads="not in the float"
        />
        {/* Everything blank is a real instruction, not an accident the seeding makes it hard to
            have: the box stops being held to any composition at all. Said only once the notes are
            actually on screen — an unloaded sheet is blank because nothing arrived, not because
            somebody blanked it, and this sentence beside it would be describing a wipe as a choice. */}
        {ready && !sheet.anythingTyped && (
          <Text size="xs" c="dimmed">
            Nothing listed means no standing float — the box holds whatever is put into it and no count is
            measured against a standing figure.
          </Text>
        )}
        <Group justify="space-between">
          <Group gap={6}>
            <Text size="sm" c="dimmed">
              It comes to
            </Text>
            <MoneyPair pair={sheet.running} fw={700} />
          </Group>
          <Button disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>
            Save the standing float
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
